import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api-error';
import { ordersClient } from '@/lib/orders-client';

/**
 * "Reimprimir": queue a thermal reprint and say so. The outcome is not awaited here — it arrives on
 * `order.printing` through the board feed, which is what the print chip reads.
 */
export function useReprint() {
  const { t } = useTranslation('orders');
  const [reprintingId, setReprintingId] = useState<string | null>(null);

  /** 409 `ORDER_PRINT_UNAVAILABLE` carries `reason` (top level, or under `details`): say which. */
  const unavailableMessage = (error: ApiError): string => {
    const details = error.data.details as { reason?: unknown } | undefined;
    const reason = error.data.reason ?? details?.reason;
    return typeof reason === 'string'
      ? t(`print.errors.unavailableReasons.${reason}`, { defaultValue: t('print.errors.unavailable') })
      : t('print.errors.unavailable');
  };

  const reprint = async (orderId: string) => {
    setReprintingId(orderId);
    try {
      await ordersClient.requestPrint(orderId);
      toast.success(t('print.sent'));
    } catch (error) {
      console.error('Failed to request a reprint:', error);
      const code = error instanceof ApiError ? error.code : null;
      toast.error(
        code === 'ORDER_NOT_ACCEPTED'
          ? t('print.errors.notAccepted')
          : code === 'ORDER_PRINT_UNAVAILABLE'
            ? unavailableMessage(error as ApiError)
            : t('print.errors.failed'),
      );
    } finally {
      setReprintingId(null);
    }
  };

  return { reprint, reprintingId };
}
